import React, { useEffect, useMemo, useState } from 'react';

interface MappingStepProps {
  rows: any[];
  onDone: (mapping: any) => void;
  containsDates?: boolean;
}

const TARGET_FIELDS = [
  { value: 'customer', labels: ['customer', 'customer name', 'name', 'client'] },
  { value: 'date', labels: ['date', 'service date', 'work date'] },
  { value: 'service', labels: ['service', 'service type', 'item', 'item name'] },
  { value: 'quantity', labels: ['quantity', 'qty', 'hours', 'units'] },
  { value: 'rate', labels: ['rate', 'price', 'amount per', 'unit rate'] },
];

function normalize(s: string) {
  return (s || '').toLowerCase().replace(/[_\-]/g, ' ').trim();
}

export default function MappingStep({ rows, onDone, containsDates = true }: MappingStepProps) {
  const [mapping, setMapping] = useState<any>({});

  const headers = useMemo(() => Object.keys(rows?.[0] || {}), [rows]);

  // Auto-detect on mount
  useEffect(() => {
    if (!headers.length) return;
    const auto: any = {};
    for (const h of headers) {
      const hn = normalize(h);
      for (const target of TARGET_FIELDS) {
        if (target.labels.some(label => hn.includes(label))) {
          auto[h] = target.value;
          break;
        }
      }
    }
    setMapping(auto);
  }, [headers]);

  const handleMappingChange = (csvColumn: string, targetField: string) => {
    setMapping((prev: any) => ({
      ...prev,
      [csvColumn]: targetField
    }));
  };

  const handleContinue = () => {
    // Build mapping structure
    const reverse: any = {};
    for (const [col, tgt] of Object.entries(mapping)) reverse[tgt as string] = reverse[tgt as string] ? [...reverse[tgt as string], col] : [col];

    if (containsDates) {
      const dateColumns = Array.isArray(reverse.date) ? reverse.date : (reverse.date ? [reverse.date] : []);
      onDone({
        customer: reverse.customer?.[0],
        date: reverse.date?.[0],
        dateColumns,
        service: reverse.service?.[0],
        quantity: reverse.quantity?.[0],
        rate: reverse.rate?.[0],
      });
    } else {
      const serviceColumns = Array.isArray(reverse.service) ? reverse.service : (reverse.service ? [reverse.service] : []);
      onDone({
        customer: reverse.customer?.[0],
        serviceColumns,
        quantity: reverse.quantity?.[0],
        rate: reverse.rate?.[0]
      });
    }
  };

  if (!rows || rows.length === 0) {
    return <div>No data to map</div>;
  }

  const targets = containsDates ? TARGET_FIELDS : TARGET_FIELDS.filter(t => t.value !== 'date');

  return (
    <div className="max-w-4xl mx-auto p-6 rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
      <h1 className="text-2xl font-bold mb-6">Map CSV Columns</h1>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h2 className="text-lg font-semibold mb-4">CSV Columns</h2>
          {headers.map((header: string, index: number) => (
            <div key={index} className="px-3 py-2 rounded-xl border border-gray-300 bg-white shadow-sm mb-2">
              {header}
            </div>
          ))}
        </div>
        <div>
          <h2 className="text-lg font-semibold mb-4">Target Fields</h2>
          {headers.map((header: string, index: number) => (
            <select
              key={index}
              value={mapping[header] || ''}
              onChange={(e) => handleMappingChange(header, e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 mb-2"
            >
              <option value="">Select field...</option>
              {targets.map(t => (
                <option key={t.value} value={t.value}>{t.value[0].toUpperCase() + t.value.slice(1)}</option>
              ))}
            </select>
          ))}
        </div>
      </div>
      <button
        onClick={handleContinue}
        className="mt-6 rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
      >
        Continue
      </button>
    </div>
  );
} 